import './BranchRegistrationWorkflowAddForm.pcss';

import { EyeOutlined, LoadingOutlined } from '@ant-design/icons';
import { Button, Form, Select } from 'custom-test-antd';
import { Formik } from 'formik';
import { FC, useEffect, useState, useCallback } from 'react';

import { Portal } from 'api/organization/onboardingTypes';
import { InputPasswordWrapper, InputWrapper } from 'utils/form-helpers/antd-formik';

import { getPortalDescription } from './utils';
import { getValidationSchema } from '../utils/validations';

interface BranchRegistrationWorkflowAddFormProps {
  branchRegistrationWorkflowsCount: number;
  onCreateBranchRegistrationWorkflow: (values: { portal: Portal; login: string; password: string }) => Promise<void>;
}

export const BranchRegistrationWorkflowAddForm: FC<BranchRegistrationWorkflowAddFormProps> = ({
  branchRegistrationWorkflowsCount,
  onCreateBranchRegistrationWorkflow,
}) => {
  const [portal, setPortal] = useState<Portal | undefined>(undefined);
  const [showSecondInput, setShowSecondInput] = useState(false);
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  useEffect(() => {
    setShowSecondInput(false);
    setIsValidating(false);
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
      setDebounceTimeout(null);
    }
  }, [portal]);

  const handleFieldValidation = useCallback(async (
    fieldName: string,
    value: string,
    validateField: any,
    values: { login: string; password: string },
    submitForm: () => Promise<void>,
  ) => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    // Show validation spinner
    setIsValidating(true);

    const timeout = setTimeout(async () => {
      try {
        await validateField(fieldName);

        if (fieldName === 'login' && value.trim()) {
          setShowSecondInput(true);
        }

        if (fieldName === 'password' && value.trim() && showSecondInput) {
          // Validate both fields before auto-submit
          await validateField('login');
          if (values.login.trim() && values.password.trim()) {
            await submitForm();
          }
        }
      } catch (error) {
        // Validation failed, do nothing
      } finally {
        // Hide validation spinner
        setIsValidating(false);
      }
    }, 1000);

    setDebounceTimeout(timeout);
  }, [debounceTimeout, showSecondInput]);

  return (
    <div className="add-credential-form">
      <div className="form-header">
        <Select
          value={portal}
          onChange={(value) => {
            setPortal(value);
          }}
          className="facility-select"
          placeholder="Select a portal"
        >
          <Select.Option value={Portal.Riayati}>Riayati</Select.Option>
          <Select.Option value={Portal.Shafafiya}>Shafafiya</Select.Option>
          <Select.Option value={Portal.eClaimLink}>eClaimLink</Select.Option>
        </Select>
        <Button
          type="text"
          className="remove-button"
          onClick={() => {
            setPortal(undefined);
            setShowSecondInput(false);
            setIsValidating(false);
            if (debounceTimeout) {
              clearTimeout(debounceTimeout);
              setDebounceTimeout(null);
            }
          }}
        >
          Cancel
        </Button>
      </div>
      {portal && (
        <>
          <div className="facility-description">{getPortalDescription(portal)}</div>
          <Formik
            key={portal}
            initialValues={{
              login: '',
              password: '',
            }}
            validationSchema={getValidationSchema(portal)}
            onSubmit={async ({ login, password }, { resetForm }) => {
              if (portal) {
                await onCreateBranchRegistrationWorkflow({ portal, login, password });
                // Reset form after successful submission
                resetForm();
                setShowSecondInput(false);
              }
            }}
          >
            {({ values, validateField, submitForm, isSubmitting }) => (
              <Form layout="vertical">
                <InputWrapper
                  name="login"
                  placeholder={portal === Portal.Riayati ? 'Enter license' : 'Enter username'}
                  size="large"
                  antFieldProps={{ label: portal === Portal.Riayati ? 'License' : 'Username' }}
                  onChange={(e) => {
                    const nextLogin = e.target.value;
                    handleFieldValidation(
                      'login',
                      nextLogin,
                      validateField,
                      { ...values, login: nextLogin },
                      submitForm,
                    );
                  }}
                />
                {showSecondInput && (
                  <InputPasswordWrapper
                    name="password"
                    placeholder={portal === Portal.Riayati ? 'Enter API key' : '••••••'}
                    size="large"
                    antFieldProps={{ label: portal === Portal.Riayati ? 'API Key' : 'Password' }}
                    iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeOutlined />)}
                    onChange={(e) => {
                      const nextPassword = e.target.value;
                      handleFieldValidation(
                        'password',
                        nextPassword,
                        validateField,
                        { ...values, password: nextPassword },
                        submitForm,
                      );
                    }}
                  />
                )}
                {isValidating && (
                  <div className="validation-indicator">
                    <LoadingOutlined />
                    {' '}
                    Validating...
                  </div>
                )}
                {isSubmitting && <div className="submitting-indicator">Submitting...</div>}
              </Form>
            )}
          </Formik>
        </>
      )}
    </div>
  );
};
